import { IAdminUser } from '@dadohr/shared/domainTypes/adminUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { USER_ROLE } from '@dadohr/shared/domainTypes/permissions';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography
} from '@material-ui/core';
import { useMemo, useState } from 'react';
import { InviteAdminUserDialog } from '../../features/Org/pages/AdminUsers';
import { useDialogState } from '../../hooks/useDialogState';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useAdminUsersInOrg } from '../../services/adminUsers';
import {
  isDadoEmailAddress,
  useCurrentUser,
  useIsCurrentUserDadoEmployee
} from '../../services/currentUser';
import { shareSavedView } from '../../services/savedView';
import { toFullName } from '../../services/user';
import { AutocompleteMulti } from '../Autocomplete';
import { ButtonWithPromise } from '../ButtonWithPromise';
import { DialogTitleWithTypography } from '../DialogTitleWithTypography';
import { InfoBox } from '../InfoBox';

type Props = {
  open: boolean;
  onClose: () => void;
  viewId: string;
  creatorId: string;
  alreadySharedWith: string[];
};

export const ShareViewModal = ({
  open,
  onClose,
  viewId,
  creatorId,
  alreadySharedWith
}: Props) => {
  const currentUser = useCurrentUser();
  const currentUserIsDado = useIsCurrentUserDadoEmployee();
  const inviteDialog = useDialogState();
  const [selectedAdmins, setSelectedAdmins] = useState<Doc<IAdminUser>[]>([]);
  const [admins] = useAdminUsersInOrg(currentUser.org.id);

  const eligibleAdmins = useMemo(() => {
    if (!admins) return [];
    return admins.filter((a) => {
      const role = currentUser.org.permissions[a.id]?.v2?.role;
      if (role !== USER_ROLE.ACCOUNT_ADMIN) return false;
      if (a.id === creatorId) return false;
      if (alreadySharedWith.includes(a.id)) return false;
      if (!currentUserIsDado && isDadoEmailAddress(a.data.email)) return false;
      return true;
    });
  }, [
    admins,
    creatorId,
    alreadySharedWith,
    currentUser.org.permissions,
    currentUserIsDado
  ]);

  const handleClose = () => {
    setSelectedAdmins([]);
    onClose();
  };

  const { enqueueSnackbarError } = useSnackbar();

  const handleSave = async () => {
    try {
      await shareSavedView({
        viewId,
        userIds: selectedAdmins.map((a) => a.id)
      });
      handleClose();
    } catch {
      enqueueSnackbarError('Failed to share view. Please try again.');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitleWithTypography>
          Share your saved view
        </DialogTitleWithTypography>
        <DialogContent>
          <AutocompleteMulti
            value={selectedAdmins}
            onChange={setSelectedAdmins}
            options={eligibleAdmins}
            getOptionLabel={(a) => toFullName(a.data)}
            getOptionSelected={(a, b) => a.id === b.id}
            label="Share with"
            fullWidth
            disableCloseOnSelect
          />
          <InfoBox color="yellow" margin="dense" style={{ marginTop: 16 }}>
            <Typography variant="body2">
              You can only share a saved view with Dado users with Account
              Administrator access. If you want to share with someone who
              doesn&apos;t yet have Dado access, please{' '}
              <Button
                color="primary"
                size="small"
                style={{ padding: 0, verticalAlign: 'baseline', minWidth: 0 }}
                onClick={inviteDialog.openDialog}
              >
                invite them to Dado
              </Button>
              .
            </Typography>
          </InfoBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <ButtonWithPromise
            variant="contained"
            color="primary"
            disabled={selectedAdmins.length === 0}
            onClick={handleSave}
            pending="Sharing..."
          >
            Save
          </ButtonWithPromise>
        </DialogActions>
      </Dialog>

      {inviteDialog.dialogOpen && (
        <InviteAdminUserDialog
          open={true}
          onClose={inviteDialog.closeDialog}
          currentUser={currentUser}
          currentAdmins={admins ?? []}
        />
      )}
    </>
  );
};
